import { CHECKPOINTS, STORY_MODE_CHAPTERS } from './story.js';

export const PROGRESS_STORAGE_KEY = 'ttg:story-progress';
export const PROGRESS_VERSION = 2;
export const DEFAULT_PROGRESS = Object.freeze({
  version: PROGRESS_VERSION,
  checkpointId: CHECKPOINTS[0].id,
  completed: false,
});

/** Saves contain chapter recipes and validated route milestones, never arbitrary positions. */
export function normalizeProgress(raw) {
  if (
    !raw ||
    typeof raw !== 'object' ||
    Array.isArray(raw) ||
    ![1, PROGRESS_VERSION].includes(raw.version) ||
    !CHECKPOINTS.some((checkpoint) => checkpoint.id === raw.checkpointId)
  ) {
    return { ...DEFAULT_PROGRESS };
  }
  return {
    ...(raw.version === PROGRESS_VERSION && raw.checkpointId === 'rooftop-route:start' && raw.route
      ? { route: normalizeRoute(raw.route) }
      : {}),
    version: PROGRESS_VERSION,
    checkpointId: raw.checkpointId,
    completed: raw.completed === true && raw.checkpointId === CHECKPOINTS.at(-1).id,
  };
}

export function loadProgress(storage) {
  try {
    // Resolve inside try: some browsers throw when localStorage itself is accessed.
    const target = storage === undefined ? globalThis.localStorage : storage;
    const stored = target?.getItem(PROGRESS_STORAGE_KEY);
    return normalizeProgress(stored ? JSON.parse(stored) : null);
  } catch {
    return { ...DEFAULT_PROGRESS };
  }
}

export function saveProgress(progress, storage) {
  const normalized = normalizeProgress(progress);
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    target?.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Return session progress even when persistence is unavailable or full.
  }
  return normalized;
}

export function clearProgress(storage) {
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    target?.removeItem(PROGRESS_STORAGE_KEY);
  } catch {
    // The caller can still restart its in-memory session.
  }
  return { ...DEFAULT_PROGRESS };
}

/** Resolve a fresh scene recipe so consumers cannot mutate the shared checkpoint. */
export function getResumeCheckpoint(progress) {
  const normalized = normalizeProgress(progress);
  const checkpoint = CHECKPOINTS.find((entry) => entry.id === normalized.checkpointId);
  return { ...checkpoint, inventory: [...checkpoint.inventory] };
}

/** Validate route prerequisites and whitelist delivered caption IDs. */
export function normalizeRoute(raw) {
  const chapter = STORY_MODE_CHAPTERS.find((entry) => entry.id === 'rooftop-route');
  const actions = chapter.beats.map((beat) => beat.objective.requires);
  const completed = [];
  for (const id of actions) {
    if (!Array.isArray(raw?.completed) || !raw.completed.includes(id)) break;
    completed.push(id);
  }
  const requested = Number.isInteger(raw?.objectiveIndex) ? raw.objectiveIndex : 0;
  const objectiveIndex = Math.max(0, Math.min(chapter.beats.length - 1, completed.length, requested));
  completed.splice(objectiveIndex + 1);
  const allowed = chapter.beats
    .slice(0, objectiveIndex + 1)
    .flatMap((beat) => beat.comms.map((line) => line.id));
  const heard = allowed.filter((id) => Array.isArray(raw?.heard) && raw.heard.includes(id));
  return { objectiveIndex, completed, heard };
}
